import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth, homePathFor } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { Button, Input } from '../components/ui'
import { RiSunLine, RiMoonLine } from '@remixicon/react'
import AppVersion from '../components/AppVersion'
import Logo from '../components/ui/Logo'

export default function Login() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Search and hash come along too: a person sent to sign in from the OAuth
  // consent screen must land back on it with the request intact, not on a bare
  // path that has lost every parameter.
  const origin = (
    location.state as { from?: { pathname: string; search?: string; hash?: string } }
  )?.from
  const from = origin && `${origin.pathname}${origin.search ?? ''}${origin.hash ?? ''}`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const signedIn = await login(email, password)
      navigate(from ?? homePathFor(signedIn), { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Theme toggle in top right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
          aria-label="Toggle theme"
        >
          {resolvedTheme === 'dark' ? (
            <RiSunLine className="size-5" />
          ) : (
            <RiMoonLine className="size-5" />
          )}
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo and title */}
          <div className="text-center">
            <Logo size="lg" />
            <h1 className="mt-8 font-display text-display text-gray-950 dark:text-gray-50">
              {t('auth.loginTitle')}
            </h1>
          </div>

          {/* Login form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <Input
                label={t('auth.email')}
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
              />

              <Input
                label={t('auth.password')}
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                enableShowPassword
              />
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              loading={isLoading}
              variant="cta"
              className="w-full"
            >
              {t('auth.login')}
            </Button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {t('auth.noAccount')}{' '}
              <Link
                to="/register"
                className="font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
              >
                {t('auth.register')}
              </Link>
            </p>
          </form>
        </div>
      </div>
      <AppVersion className="pb-4 text-center" />
    </div>
  )
}
